import { usePagination, useActions, Page } from "@nosto/search-js/preact/hooks";
import { JSX } from "preact/jsx-runtime";
import style from "./Pagination.module.css";
import { getPageUrl } from "@/mapping/url/getPageUrl";
import { cl } from "@nosto/search-js/utils";

type Props = {
  ariaLabel?: string;
} & JSX.IntrinsicElements["a"];

function PageLink({ onClick, href, ariaLabel, children }: Props) {
  return (
    <a href={href} aria-label={ariaLabel} onClick={onClick}>
      {children}
    </a>
  );
}

export default function Pagination() {
  const { prev, first, pages, last, next } = usePagination({
    width: 5,
  });

  const { updateSearch } = useActions();

  function pageLinkProps({ from, page }: Page) {
    return {
      href: getPageUrl(page),
      onClick: (e: Event) => {
        e.preventDefault();
        updateSearch({ products: { from } });
        scrollTo(0, 0);
      },
    };
  }

  if (pages.length === 1) {
    return <></>;
  }

  return (
    <ul className="plp__pagination-list">
      {prev && (
        <li className="previous-page">
          <PageLink {...pageLinkProps(prev)} ariaLabel="Previous page">
            Prev
          </PageLink>
        </li>
      )}

      {first && (
        <>
          <li>
            <PageLink {...pageLinkProps(first)} ariaLabel="First page">
              {first.page}
            </PageLink>
          </li>
          <li>
            <span className={style.filler}>...</span>
          </li>
        </>
      )}

      {pages.map((page) => (
        <li key={page.page} className={cl(page.current && "selected")}>
          <PageLink ariaLabel={`${page.page} page`} {...pageLinkProps(page)}>
            {page.page}
          </PageLink>
        </li>
      ))}

      {last && (
        <>
          <li>
            <span className={style.filler}>...</span>
          </li>
          <li>
            <PageLink {...pageLinkProps(last)} ariaLabel="Last page">
              {last.page}
            </PageLink>
          </li>
        </>
      )}

      {next && (
        <li className="next-page">
          <PageLink {...pageLinkProps(next)} ariaLabel="Next page">
            Next
          </PageLink>
        </li>
      )}
    </ul>
  );
}
